import { useState, useRef, useEffect, useMemo } from "react";
import { mockStudies } from "@/mocks/studiesData";

interface StudyCodeSelectProps {
  value: string;
  studyName?: string;
  onChange: (code: string) => void;
  onNewStudy: () => void;
  hasError?: boolean;
  placeholder?: string;
}

export default function StudyCodeSelect({
  value,
  studyName,
  onChange,
  onNewStudy,
  hasError,
  placeholder = "Select Study",
}: StudyCodeSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Compute dropdown position when opening
  useEffect(() => {
    if (!open || !triggerRef.current) return;

    const updatePos = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      setPos({
        top: rect.bottom + 6,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePos();
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [open]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return mockStudies;
    return mockStudies.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.code.toLowerCase().includes(q)
    );
  }, [query]);

  const selectedStudy = useMemo(
    () => mockStudies.find((s) => s.code === value),
    [value]
  );

  const displayLabel = useMemo(() => {
    if (!value) return placeholder;
    if (selectedStudy) return `${selectedStudy.name} (${selectedStudy.code})`;
    if (studyName) return `${studyName} (${value})`;
    return value;
  }, [value, selectedStudy, studyName, placeholder]);

  return (
    <div>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full h-11 px-3 text-sm border rounded-lg outline-none flex items-center justify-between cursor-pointer transition-colors ${
          hasError ? "border-red-400 bg-red-50" : "border-gray-200 bg-white"
        } ${open ? "border-pink-400" : ""}`}
      >
        <span className={value ? "text-gray-800" : "text-gray-400"}>
          {displayLabel}
        </span>
        <span className="w-5 h-5 flex items-center justify-center shrink-0">
          <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}></i>
        </span>
      </button>

      {open && pos && (
        <div
          ref={dropdownRef}
          className="fixed bg-white border border-gray-200 rounded-lg z-50 overflow-hidden pointer-events-none"
          style={{
            top: pos.top,
            left: pos.left,
            width: pos.width,
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
          }}
        >
          <div className="px-3 py-2 border-b border-gray-100 pointer-events-auto">
            <div className="flex items-center gap-2 bg-gray-50 rounded-md px-2.5 py-2">
              <span className="w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search study name or code..."
                className="flex-1 bg-transparent text-sm outline-none text-gray-700 placeholder:text-gray-400"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="max-h-52 overflow-y-auto pointer-events-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-gray-400 text-center">
                No studies found
              </div>
            ) : (
              filtered.map((study) => (
                <button
                  key={study.id}
                  type="button"
                  onClick={() => {
                    onChange(study.code);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={`w-full px-3 py-2.5 text-sm text-left cursor-pointer hover:bg-pink-50 transition-colors ${
                    value === study.code ? "text-[#830051] font-semibold bg-pink-50" : "text-gray-700"
                  }`}
                >
                  <div className="font-medium">{study.name}</div>
                  <div className="text-xs text-gray-400 font-mono mt-0.5">{study.code}</div>
                </button>
              ))
            )}
          </div>

          <div className="px-3 py-2 border-t border-gray-100 pointer-events-auto">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onNewStudy();
              }}
              className="w-full flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-[#830051] hover:bg-pink-50 rounded-md transition-colors cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line"></i>
              </span>
              New Study
            </button>
          </div>
        </div>
      )}
    </div>
  );
}